import { useEffect, useRef, useState } from "react";
import { EVENTOS, capturarOrigen, cargarPixel, track } from "@/lib/captacion";
import { BarrasFlotantes } from "@/components/landing/BarrasFlotantes";
import { ComoEmpiezas } from "@/components/landing/ComoEmpiezas";
import { ConfianzaPreguntas } from "@/components/landing/ConfianzaPreguntas";
import { DemoForm } from "@/components/landing/DemoForm";
import { Hero } from "@/components/landing/Hero";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { LandingNav } from "@/components/landing/LandingNav";
import { MarcaPropia } from "@/components/landing/MarcaPropia";
import { MesResuelto } from "@/components/landing/MesResuelto";
import { Modulos } from "@/components/landing/Modulos";
import { Planes } from "@/components/landing/Planes";
import { Promesa } from "@/components/landing/Promesa";

const ALTO_NAV = 64;

// El hero queda FIJO y el resto de la página sube por encima, plano, como xpendit.com. Es CSS: el
// hero es `position: sticky` con `z-0` y todo lo que sigue va en una capa `relative z-10` con fondo
// propio. (Se probó con esquinas redondeadas, sombra y hero atenuado: el usuario lo descartó.)
//
// El `top` no es 0 fijo: se calcula. En desktop el hero cabe en la pantalla y se clava bajo la
// navbar. En mobile mide más que la pantalla, y con `top: 0` se congelaría ANTES de mostrar el
// botón y el mockup: ahí el `top` es negativo (alto de pantalla − alto del hero), así que primero
// se recorre entero y recién se fija por su borde inferior.
const HeroFijo = ({ children }) => {
  const ref = useRef(null);
  const [top, setTop] = useState(ALTO_NAV);

  useEffect(() => {
    const medir = () => {
      const alto = ref.current?.offsetHeight || 0;
      setTop(Math.min(ALTO_NAV, window.innerHeight - alto));
    };
    medir();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    if (ro && ref.current) ro.observe(ref.current);
    window.addEventListener("resize", medir);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, []);

  return (
    <div ref={ref} className="sticky z-0" style={{ top }} data-testid="hero-fijo">
      {children}
    </div>
  );
};

// Landing pública de sebbia.cl. No usa Clerk ni ningún provider de la app: vive en el bundle
// inicial y se pinta sin esperar la sesión (ver `Root.jsx`).
//
// El recorrido: qué es (hero) → cómo me resuelve el mes (única franja oscura) → qué gano (promesa)
// → qué más trae (módulos) → se ve como mi marca (marca propia) → cuánto me cuesta cambiarme →
// cuánto cuesta → ¿puedo confiar? → demo. Un solo CTA primario, "Agenda una demo".
export default function LandingPage() {
  const [planInteres, setPlanInteres] = useState("");

  useEffect(() => {
    document.title = "SEBBIA — Administración de arriendos para corredoras";
    // Anclas con desplazamiento suave, salvo que el visitante haya pedido menos movimiento.
    const reducido = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!reducido) document.documentElement.style.scrollBehavior = "smooth";
    capturarOrigen();
    cargarPixel();
    track(EVENTOS.vistaContenido, { content_name: "landing" });
    return () => { document.documentElement.style.scrollBehavior = ""; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 antialiased" data-testid="landing-page">
      <a
        href="#demo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-xl focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-md"
      >
        Saltar al formulario de demo
      </a>
      <LandingNav />
      <main>
        <HeroFijo>
          <Hero />
        </HeroFijo>
        <div className="relative z-10">
          <MesResuelto />
          <Promesa />
          <Modulos />
          <MarcaPropia />
          <ComoEmpiezas />
          <Planes onElegirPlan={setPlanInteres} />
          <ConfianzaPreguntas />
          <DemoForm planInteres={planInteres} />
          <LandingFooter />
        </div>
      </main>
      <BarrasFlotantes />
    </div>
  );
}
